import { addDays, daysBetween, isNonWorkingDay } from './dates.js';
/**
 * Performance Review Procedure rules (see docs/PERFORMANCE-REMUNERATION.md, sections 2–5).
 * The review period is a calendar year; its meetings run Feb–Mar of the following year.
 */
export const RATING_LABEL = { 5: 'Outstanding', 4: 'Exceeding', 3: 'Meeting', 2: 'Partially meeting', 1: 'Not meeting' };
export const DIMENSIONS = [
    { key: 'd1', name: 'Goals from the prior year', help: 'Form 3 goals, one by one. Superseded goals do not count against the employee.' },
    { key: 'd2', name: 'Role accountabilities', help: 'Each accountability in the current PD. 1 or 2 here caps overall at 2.' },
    { key: 'd3', name: 'Capability, credentials & PD', help: 'Trainers: evidence for Standards 3.2 and 3.3.' },
    { key: 'd4', name: 'Values & behaviours (SAFER)', help: '1 or 2 here caps overall at 3. Blocks 4 and 5 if values inconsistent.' },
    { key: 'd5', name: 'Student, client & compliance outcomes', help: 'Only outcomes the employee could reasonably influence.' },
];
/** PTI's values (IMS-SEC-004 wording, the authoritative source). One table, used everywhere. */
export const SAFER_VALUES = [
    { key: 'S', name: 'Safety', line: 'We teach safety because we live it first.' },
    { key: 'A', name: 'Accountability', line: 'We own our actions, our standards, and our outcomes.' },
    { key: 'F', name: 'Forward-thinking', line: 'We stay current so our learners are always ahead.' },
    { key: 'E', name: 'Excellence', line: 'We deliver practical training that works in the real world.' },
    { key: 'R', name: 'Respect', line: 'Every learner deserves our best — without exception.' },
];
export const CYCLE_STEPS = [
    { n: 1, title: 'Confirm readiness', when: 'Late Jan', months: [0] },
    { n: 2, title: 'Open the cycle — issue Form 1', when: 'Early Feb', months: [1] },
    { n: 3, title: 'Employee self-assessment', when: 'Feb', months: [1] },
    { n: 4, title: 'Reviewer preparation — draft Form 2', when: 'Feb', months: [1] },
    { n: 5, title: 'Currency check (trainers)', when: 'Feb', months: [1] },
    { n: 6, title: 'Review meeting', when: 'Feb–Mar', months: [1, 2] },
    { n: 7, title: 'Employee reply (5 working days)', when: 'After meeting', months: [2] },
    { n: 8, title: 'Calibration — Form 5', when: 'Mid Mar', months: [2] },
    { n: 9, title: 'Finalise and sign Form 2', when: 'Late Mar', months: [2] },
    { n: 10, title: 'Confirm development plans — Form 3', when: 'Late Mar', months: [2] },
    { n: 11, title: 'Hand ratings to remuneration', when: 'By 30 Apr', months: [3] },
    { n: 12, title: 'Mid-year check-in — Form 4', when: 'Aug–Sep', months: [7, 8] },
    { n: 13, title: 'Report and close', when: 'Oct', months: [9] },
];
export const CALIBRATION_TESTS = [
    'Same standard', 'Evidence sufficiency', 'Recency', 'Halo and horns', 'Central tendency',
    'Leniency and severity', 'Proximity', 'No-surprises', 'Evidence for every proposed 5',
];
export const GOAL_STATUS = { on: 'On track', risk: 'At risk', off: 'Off track', done: 'Achieved', superseded: 'Superseded' };
/** P-6: dimension caps on the overall rating. The overall is a judgement (P-7) — never calculated from the dimensions. */
export function ratingCap(dims) {
    let cap = 5;
    const reasons = [];
    const d2 = dims.d2 ?? null;
    const d4 = dims.d4 ?? null;
    if (d2 != null && d2 <= 2) {
        cap = Math.min(cap, 2);
        reasons.push(`Role accountabilities rated ${d2} — overall capped at 2`);
    }
    if (d4 != null && d4 <= 2) {
        cap = Math.min(cap, 3);
        reasons.push(`Values rated ${d4} — overall capped at 3`);
    }
    return { cap, reasons };
}
/** P-9: a proposed 1 or 2 needs a concern recorded during the review period. */
export function noSurprisesOk(proposed, recordedConcerns) {
    return proposed == null || proposed > 2 || recordedConcerns > 0;
}
/** P-13: the Director reviewing (the RTO Manager) and proposing 1, 2 or 5 must seek an outside view. */
export function needsExternalPerspective(reviewerIsApprover, proposed) {
    return reviewerIsApprover && proposed != null && (proposed <= 2 || proposed === 5);
}
/** P-12: the employee may reply within 5 working days of the meeting (weekends and WA public holidays excluded). */
export function replyDue(meetingIso, holidays = new Set(), workingDays = 5) {
    let d = meetingIso;
    let n = 0;
    while (n < workingDays) {
        d = addDays(d, 1);
        if (!isNonWorkingDay(d, holidays))
            n++;
    }
    return d;
}
/** The live checks shown on Form 2 ("Checks the procedure requires"). */
export function reviewChecks(r) {
    const out = [];
    const { cap, reasons } = ratingCap(r.dims);
    out.push(reasons.length ? { tone: 'warn', text: reasons.join('; ') } : { tone: 'ok', text: 'No dimension caps apply.' });
    if (r.proposed != null && r.proposed > cap)
        out.push({ tone: 'bad', text: `Proposed ${r.proposed} is above the cap of ${cap}.` });
    if (r.proposed != null && r.proposed <= 2) {
        out.push(r.concerns > 0 ? { tone: 'ok', text: 'No-surprises test passes — concern recorded during the period.' }
            : { tone: 'bad', text: 'No-surprises test fails — no recorded concern in the period. A 1 or 2 can’t be proposed.' });
        out.push(r.supportPersonOffered ? { tone: 'ok', text: 'Support person offered in advance.' } : { tone: 'warn', text: 'Offer a support person in advance (required for a proposed 1 or 2).' });
    }
    if (r.proposed === 5)
        out.push({ tone: 'warn', text: 'A 5 needs specific evidence stated and recorded at calibration, or it becomes a 4.' });
    if (needsExternalPerspective(r.reviewerIsApprover, r.proposed)) {
        out.push(r.externalPerspective?.trim() ? { tone: 'ok', text: `External perspective: ${r.externalPerspective.trim()}` } : { tone: 'warn', text: 'Director is reviewer and approver — seek an external perspective before finalising.' });
    }
    out.push(r.selfAssessmentSubmitted ? { tone: 'ok', text: 'Self-assessment received before reviewer drafted.' } : { tone: 'warn', text: 'Self-assessment (Form 1) not received yet.' });
    return out;
}
/** P-2: who is in the cycle for a review period. Employees only; under 3 months' service at the cycle opening date are out (new starters set goals at commencement instead). */
export function inCycle(p, openOn) {
    if (p.roleKey === 'director')
        return { inCycle: false, reason: 'Director — outside the employee review and remuneration cycles.' };
    if (p.engagementType === 'contractor')
        return { inCycle: false, reason: 'Contractors are outside the performance and remuneration procedures. Engagement evaluated per cohort.' };
    if (!['active', 'probation', 'separating'].includes(p.stage))
        return { inCycle: false, reason: 'Not currently employed.' };
    if (p.startDate && daysBetween(p.startDate, openOn) < 91)
        return { inCycle: false, reason: 'New starter — under 3 months at the review date; Form 3 goals set at commencement.' };
    return { inCycle: true, reason: null };
}
/** Rating distribution 5→1 for calibration ("No quota applied"). */
export function distribution(ratings) {
    const d = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    for (const r of ratings)
        if (r)
            d[r] = (d[r] ?? 0) + 1;
    return d;
}
//# sourceMappingURL=performance.js.map